import { Component, EventEmitter, Input, Output, OnInit, ChangeDetectorRef } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CoreService } from '../../../services/core.service';
import { SocialPlatformI, WeeklySlotsI, RssFeedI } from '../../../interfaces/core.interface';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Router } from '@angular/router';

@Component({
  selector: 'app-social-ai-scheduler-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatCheckboxModule
  ],
  templateUrl: './social-ai-scheduler-form.component.html',
  styleUrl: './social-ai-scheduler-form.component.scss'
})
export class SocialAiSchedulerFormComponent implements OnInit {
  @Input() formData: WeeklySlotsI | WeeklySlotsI[] | null = null;
  @Output() onSubmitClbk = new EventEmitter<{ type: string; data: any }>();

  weeklySlotsForm: FormGroup;
  bulkTimeGroup: FormGroup;

  weekdays: { value: number; name: string }[] = [
    { value: 0, name: 'Monday' },
    { value: 1, name: 'Tuesday' },
    { value: 2, name: 'Wednesday' },
    { value: 3, name: 'Thursday' },
    { value: 4, name: 'Friday' },
    { value: 5, name: 'Saturday' },
    { value: 6, name: 'Sunday' }
  ];

  socialPlatforms: SocialPlatformI[] = [];
  rssFeeds: RssFeedI[] = [];

  hourOptions: string[] = [];
  minuteOptions: string[] = [];
  ampmOptions: string[] = ['AM', 'PM'];
  showSlotsView = false;
  isLoading = false; // Προσθήκη μεταβλητής για τον loader

  private currentCompanyId: number | null = null;
  private currentSiteId: number | null = null;
  deletedSlotIds: number[] = [];

  constructor(
    private coreService: CoreService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.weeklySlotsForm = this.fb.group({
      rss_feed_id: [null, Validators.required],
      social_platform_id: [null, Validators.required],
      slots: this.fb.array([])
    });

    this.bulkTimeGroup = this.fb.group({
      hour: [null],
      minute: [null],
      ampm: ['AM'],
      selectedDays: this.fb.group({
        0: [true],
        1: [true],
        2: [true],
        3: [true],
        4: [true],
        5: [true],
        6: [true]
      })
    });

    this.generateTimeOptions();
    this.initializeWeekdayGroups();
  }

  get slots(): FormArray {
    return this.weeklySlotsForm.get('slots') as FormArray;
  }

  asFormGroup(control: any): FormGroup {
    return control as FormGroup;
  }

  private initializeWeekdayGroups(): void {
    this.slots.clear();
    this.weekdays.forEach((day) => {
      this.slots.push(this.createDayGroup(day.value));
    });
  }

  private createDayGroup(weekday: number): FormGroup {
    return this.fb.group({
      weekday: [weekday],
      times: this.fb.array([])
    });
  }

  private createTimeGroup(slot?: WeeklySlotsI): FormGroup {
    const parsed = slot?.post_time ? this.parse24HourTime(slot.post_time) : null;

    return this.fb.group({
      id: [slot?.id ?? null],
      hour: [parsed?.hour ?? null, Validators.required],
      minute: [parsed?.minute ?? null, Validators.required],
      ampm: [parsed?.ampm ?? 'AM', Validators.required]
    });
  }

  getDaySlots(dayIndex: number): FormArray {
    return this.slots.at(dayIndex).get('times') as FormArray;
  }

  removeDaySlot(dayIndex: number, slotIndex: number): void {
    const daySlots = this.getDaySlots(dayIndex);
    const slotGroup = daySlots.at(slotIndex) as FormGroup;
    const slotId = slotGroup.get('id')?.value;

    if (slotId) {
      this.deletedSlotIds.push(slotId);
    }

    daySlots.removeAt(slotIndex);
  }

  clearDaySlots(dayIndex: number): void {
    const daySlots = this.getDaySlots(dayIndex);

    daySlots.controls.forEach((control) => {
      const slotId = control.get('id')?.value;
      if (slotId) {
        this.deletedSlotIds.push(slotId);
      }
    });

    daySlots.clear();
  }

  /**
   * Καθαρίζει όλα τα slots για όλες τις μέρες της εβδομάδας.
   */
  clearAllSlots(): void {
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      this.clearDaySlots(dayIndex);
    }
  }

  /**
   * Καθαρίζει τα slots για τις εργάσιμες ημέρες (Δευ-Παρ, δηλ. 0-4).
   */
  clearWeekdaySlots(): void {
    for (let dayIndex = 0; dayIndex < 5; dayIndex++) {
      this.clearDaySlots(dayIndex);
    }
  }

  addBulkSlot(): void {
    const bulkHour = this.bulkTimeGroup.get('hour')?.value;
    const bulkMinute = this.bulkTimeGroup.get('minute')?.value;
    const bulkAmpm = this.bulkTimeGroup.get('ampm')?.value;
    const selectedDays = this.bulkTimeGroup.get('selectedDays')?.value || {};

    if (!bulkHour || !bulkMinute) {
      this.coreService.showSnackbar('Please choose a time first.');
      return;
    }

    this.weekdays.forEach((day) => {
      if (!selectedDays[day.value]) return;

      const daySlots = this.getDaySlots(day.value);
      const exists = daySlots.controls.some((control) => {
        return (
          control.get('hour')?.value === bulkHour &&
          control.get('minute')?.value === bulkMinute &&
          control.get('ampm')?.value === bulkAmpm
        );
      });

      if (!exists) {
        daySlots.push(
          this.fb.group({
            id: [null],
            hour: [bulkHour, Validators.required],
            minute: [bulkMinute, Validators.required],
            ampm: [bulkAmpm, Validators.required]
          })
        );
      }
    });
  }

  formatSlotLabel(control: any): string {
    const hour = control.get('hour')?.value;
    const minute = control.get('minute')?.value;
    const ampm = control.get('ampm')?.value;

    if (!hour || !minute) return '';
    return `${hour}:${minute} ${ampm}`;
  }

  private generateTimeOptions(): void {
    for (let h = 1; h <= 12; h++) {
      this.hourOptions.push(h.toString().padStart(2, '0'));
    }
    for (let m = 0; m < 60; m++) {
      this.minuteOptions.push(m.toString().padStart(2, '0'));
    }
  }

  ngOnInit(): void {
    this.coreService.getData('/getSocialPlatforms').subscribe((data: any) => {
      this.socialPlatforms = data.data || [];
      this.populateFormData();
    });

    this.coreService.getData('/getRssFeeds').subscribe((data: any) => {
      this.rssFeeds = data.data || [];
    });
  }

  private populateFormData(): void {
    if (!this.formData) return;

    const rawFormData = Array.isArray(this.formData) ? this.formData : [this.formData];

    const formDataArray = rawFormData.map((slot: any) => ({
      ...slot,
      id: slot.id ?? slot.weekly_slot_id ?? null,
      post_time: slot.post_time ?? slot.time ?? null,
      social_platform_id: slot.social_platform_id ?? null,
      rss_feed_id: slot.rss_feed_id ?? this.weeklySlotsForm.get('rss_feed_id')?.value ?? null,
      companyID: slot.companyID ?? this.currentCompanyId ?? null,
      site_id: slot.site_id ?? this.currentSiteId ?? null
    }));

    if (formDataArray.length === 0) return;

    this.currentCompanyId = formDataArray[0].companyID ?? null;
    this.currentSiteId = formDataArray[0].site_id ?? null;

    this.weeklySlotsForm.patchValue({
      rss_feed_id: formDataArray[0].rss_feed_id,
      social_platform_id: formDataArray[0].social_platform_id
    });

    this.deletedSlotIds = [];
    this.initializeWeekdayGroups();

    const platformId = this.weeklySlotsForm.get('social_platform_id')?.value;

    this.weekdays.forEach((day) => {
      const daySlots = this.getDaySlots(day.value);

      formDataArray
        .filter((slot: any) =>
          slot.weekday === day.value &&
          slot.social_platform_id == platformId &&
          !!slot.post_time?.trim()
        )
        .forEach((slot: any) => {
          daySlots.push(this.createTimeGroup(slot));
        });
    });
  }


  private parse24HourTime(postTime24hr: string): { hour: string; minute: string; ampm: string } {
    const [h24Str, mStr] = postTime24hr.substring(0, 5).split(':');
    const h24 = parseInt(h24Str, 10);
    const m = parseInt(mStr, 10);

    let displayHour = h24;
    let ampm = 'AM';

    if (h24 === 0) {
      displayHour = 12;
    } else if (h24 === 12) {
      ampm = 'PM';
    } else if (h24 > 12) {
      displayHour = h24 - 12;
      ampm = 'PM';
    }

    return {
      hour: displayHour.toString().padStart(2, '0'),
      minute: m.toString().padStart(2, '0'),
      ampm
    };
  }

  private to24HourTime(hourInput: string, minuteInput: string, ampmInput: string): string {
    let h24 = parseInt(hourInput, 10);
    const m = parseInt(minuteInput, 10);

    if (ampmInput === 'PM' && h24 !== 12) h24 += 12;
    if (ampmInput === 'AM' && h24 === 12) h24 = 0;

    return `${h24.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:00`;
  }

  getPlatformName(platformId: number | null): string {
    if (platformId === null || this.socialPlatforms.length === 0) {
      return '';
    }

    const platform = this.socialPlatforms.find((p) => p.id === platformId);
    return platform ? platform.platform : 'Unknown Platform';
  }

  async onSubmit(): Promise<void> {
    if (this.isLoading) return;

    this.isLoading = true;
    this.cdr.detectChanges();

    await new Promise((resolve) => setTimeout(resolve, 0));

    this.weeklySlotsForm.markAllAsTouched();

    try {
      if (
        this.weeklySlotsForm.get('rss_feed_id')?.invalid ||
        this.weeklySlotsForm.get('social_platform_id')?.invalid
      ) {
        this.coreService.showSnackbar('Please select a Category and Social Platform.');
        return;
      }

      if (this.currentCompanyId === null || this.currentSiteId === null) {
        this.coreService.showSnackbar('Site ID or Company ID missing. Cannot save slots.');
        return;
      }

      const rssFeedId = this.weeklySlotsForm.get('rss_feed_id')?.value;
      const socialPlatformId = this.weeklySlotsForm.get('social_platform_id')?.value;

      const results: any[] = [];
      let hasError = false;
      const seenSlots = new Set<string>();

      for (let dayIndex = 0; dayIndex < this.slots.length; dayIndex++) {
        const daySlots = this.getDaySlots(dayIndex);

        for (let slotIndex = 0; slotIndex < daySlots.length; slotIndex++) {
          const slotGroup = daySlots.at(slotIndex) as FormGroup;
          const slotId = slotGroup.get('id')?.value;
          const hourInput = slotGroup.get('hour')?.value;
          const minuteInput = slotGroup.get('minute')?.value;
          const ampmInput = slotGroup.get('ampm')?.value;

          if (!hourInput || !minuteInput) continue;

          const normalizedTime = this.to24HourTime(hourInput, minuteInput, ampmInput);
          const slotKey = `${dayIndex}-${normalizedTime}`;

          if (seenSlots.has(slotKey)) {
            this.coreService.showSnackbar(
              `Duplicate time on ${this.weekdays[dayIndex].name}: ${hourInput}:${minuteInput} ${ampmInput}`
            );
            return;
          }

          seenSlots.add(slotKey);

          const payload: WeeklySlotsI = {
            id: slotId || undefined,
            rss_feed_id: rssFeedId,
            weekday: dayIndex as 0 | 1 | 2 | 3 | 4 | 5 | 6,
            post_time: normalizedTime,
            social_platform_id: socialPlatformId,
            status: 'active',
            companyID: this.currentCompanyId,
            site_id: this.currentSiteId
          };

          const endpoint = slotId ? `updateWeeklySlot/${slotId}` : 'addWeeklySlot';

          try {
            const res = await this.coreService.postData(endpoint, payload).toPromise();
            results.push(res);
          } catch (err) {
            hasError = true;
            console.error(`Error on weekday ${dayIndex}, slot ${slotIndex}:`, err);
          }
        }
      }

      for (const slotId of this.deletedSlotIds) {
        try {
          const del = await this.coreService.deleteData('deleteWeeklySlot', slotId).toPromise();
          results.push(del);
        } catch (err) {
          hasError = true;
          console.error(`Error deleting slot ${slotId}:`, err);
        }
      }

      if (hasError) {
        this.coreService.showSnackbar('Some weekly slots failed to save.');
        return;
      }

      this.deletedSlotIds = [];

      this.coreService.showSnackbar('Weekly slots saved successfully!');

      this.onSubmitClbk.emit({
        type: 'weeklySlot',
        data: {
          rss_feed_id: rssFeedId,
          social_platform_id: socialPlatformId,
          responses: results
        }
      });
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

}
